import { dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { faAngleDown, faAngleRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { useKanbanBoardColumnStyles } from 'components/UI/V2/KanbanBoard/Column/KanbanBoardColumn.styles';
import React from 'react';

export interface KanbanBoardMember {
    id: string;
    name: string;
    avatar: string;
}

interface KanbanBoardColumnProps {
    id: string;
    title: string;
    count?: number;
    filter?: React.ReactNode;
    subtitle?: React.ReactNode;

    members?: Array<KanbanBoardMember>;
    onMembersClick?: () => void;
    onMemberClick?: (member: KanbanBoardMember) => void;
    headerStart?: React.ReactNode;
    acceptsDrop?: boolean;

    className?: string;
    children?: React.ReactNode;
}

export const KanbanBoardColumn: React.FC<KanbanBoardColumnProps> = ({
    id,
    title,
    count,
    filter,
    subtitle,
    members,
    onMembersClick,
    onMemberClick,
    headerStart,
    acceptsDrop = true,
    className,
    children
}) => {
    const classes = useKanbanBoardColumnStyles();

    const rootRef = React.useRef<HTMLDivElement>(null);

    const [isCollapsed, setIsCollapsed] = React.useState(false);
    const [isDropTarget, setIsDropTarget] = React.useState(false);

    React.useEffect(() => {
        const element = rootRef.current;
        if (!element || !acceptsDrop) return;

        return dropTargetForElements({
            element,
            getData: () => ({
                type: 'column',
                columnId: id
            }),
            canDrop: ({ source }) => source.data.type === 'kanban-card',
            onDragEnter: () => {
                setIsDropTarget(true);
            },
            onDragLeave: () => {
                setIsDropTarget(false);
            },
            onDrop: () => {
                setIsDropTarget(false);
            }
        });
    }, [id, acceptsDrop]);

    const collapseHandler = () => {
        // Only collapse on mobile
        if (window.matchMedia('(min-width: 600px)').matches) {
            return;
        }

        setIsCollapsed(!isCollapsed);
    };

    return (
        <div
            ref={rootRef}
            data-column-id={id}
            className={clsx(classes.root, className, { [classes.collapsed]: isCollapsed })}
        >
            <div className={classes.header}>
                {headerStart}
                <div className={classes.titleGroup}>
                    <p
                        className={classes.title}
                        onClick={collapseHandler}
                    >
                        <span className={classes.titleText}>{title}</span>

                        {members && (
                            <div
                                className={classes.members}
                                onClick={onMembersClick}
                                data-clickable={!!onMembersClick}
                            >
                                {members.slice(0, 3).map(member => (
                                    <img
                                        key={member.id}
                                        src={member.avatar}
                                        alt={member.name}
                                    />
                                ))}

                                {members.length > 3 && (
                                    <span className={classes.membersCount}>+{members.length - 3}</span>
                                )}
                            </div>
                        )}

                        {count !== undefined && <span className={classes.counter}>{count}</span>}
                    </p>
                    {subtitle && <div className={classes.subtitle}>{subtitle}</div>}
                </div>

                {filter && <div className={classes.filter}>{filter}</div>}

                <FontAwesomeIcon
                    icon={isCollapsed ? faAngleRight : faAngleDown}
                    onClick={collapseHandler}
                />
            </div>

            {!isCollapsed && (
                <div
                    data-column-content={id}
                    className={clsx(classes.content, {
                        [classes.contentDropTarget]: isDropTarget
                    })}
                >
                    {children}
                </div>
            )}
        </div>
    );
};
